// Who is sending a customer offer from the rate desk, read from the signed-in session. Pure, so the panel
// and its tests read it the same way. The Worker does not trust any of this: it verifies the token itself
// and stores the account it belongs to (see offers-api.ts). This only fills in what the customer reads.

export interface OfferSeller {
  /** The person: their name when the account has one, else their email. */
  name: string
  email?: string
  /** The forwarder they sell for: the account's company when it has one, else their email's domain. */
  member: string
}

/** The parts of a Supabase user (or of its access token's claims) this reads. */
export interface SessionUser {
  email?: string | null
  user_metadata?: Record<string, unknown> | null
}

// Mailbox providers, not companies: a forwarder is never called "Gmail".
const MAILBOX_DOMAINS = new Set([
  '126.com',
  '163.com',
  'aol.com',
  'foxmail.com',
  'gmail.com',
  'gmx.de',
  'gmx.net',
  'googlemail.com',
  'hotmail.com',
  'icloud.com',
  'live.com',
  'mail.ru',
  'me.com',
  'msn.com',
  'outlook.com',
  'proton.me',
  'protonmail.com',
  'qq.com',
  'sina.com',
  'yahoo.com',
  'yandex.ru',
  'yeah.net',
])
// Second-level labels that are part of a country suffix (acme.co.uk, acme.com.cn), not the company.
const SUFFIX_LABELS = new Set(['ac', 'co', 'com', 'edu', 'gov', 'ltd', 'net', 'or', 'org'])

const titleCase = (s: string) =>
  s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

/** "ops@harbour-line.co.uk" → "Harbour Line". Null for a mailbox provider or an address with no domain. */
export function forwarderFromEmail(email: string | null | undefined): string | null {
  const domain = email?.split('@')[1]?.trim().toLowerCase()
  if (!domain || MAILBOX_DOMAINS.has(domain)) return null
  const labels = domain.split('.').filter(Boolean)
  if (labels.length < 2) return null
  labels.pop()
  if (labels.length > 1 && SUFFIX_LABELS.has(labels[labels.length - 1] ?? '')) labels.pop()
  const name = titleCase(labels[labels.length - 1] ?? '')
  return name || null
}

const str = (v: unknown, max: number): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : undefined

/** The seller shown on the offer. Field lengths match what the offer reader accepts. */
export function sellerFromUser(user: SessionUser): OfferSeller | null {
  const email = str(user.email, 120)
  const meta = user.user_metadata ?? {}
  const name = str(meta.full_name, 80) ?? str(meta.name, 80) ?? email?.slice(0, 80)
  if (!name) return null
  const member =
    str(meta.company, 60) ??
    str(meta.company_name, 60) ??
    str(meta.organization, 60) ??
    str(meta.org_name, 60) ??
    forwarderFromEmail(email)?.slice(0, 60) ??
    name.slice(0, 60)
  return { name, ...(email ? { email } : {}), member }
}

/**
 * The claims inside an access token, read WITHOUT verifying it: only to show who the panel will send
 * as. The Worker verifies the token with Supabase before it accepts anything.
 */
export function tokenClaims(token: string): (SessionUser & { iss?: string; sub?: string }) | null {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
    const bytes = Uint8Array.from(json, (c) => c.charCodeAt(0))
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as unknown
    return claims && typeof claims === 'object' ? (claims as SessionUser) : null
  } catch {
    return null
  }
}
